import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "../Logo";
import { RegisterMenu, REGISTER_OPTIONS } from "./RegisterMenu";

export const ERP_URL = "https://erp.synqsmc.com";

const sectionHref = (id) => (window.location.pathname === "/" ? `#${id}` : `/#${id}`);

const LINKS = [
  { label: "Home", href: "/", testid: "nav-link-home" },
  { label: "Features", href: sectionHref("features"), testid: "nav-link-features" },
  { label: "Pricing", href: sectionHref("pricing"), testid: "nav-link-pricing" },
  { label: "About Us", href: sectionHref("about"), testid: "nav-link-about" },
  { label: "Contact", href: sectionHref("contact"), testid: "nav-link-contact" },
];

export const PublicNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open ? "border-b border-slate-200 bg-white/85 backdrop-blur-xl" : "bg-transparent"
      } ${scrolled ? "py-3" : "py-5"}`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="/" data-testid="nav-home-logo" aria-label="SynqSMC home">
          <Logo size={38} />
        </a>
        <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <a key={l.testid} href={l.href} data-testid={l.testid}
               className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-600">
              {l.label}
            </a>
          ))}
          <span className="h-5 w-px bg-slate-200" />
          <RegisterMenu testid="nav-register-button" />
          <a href={ERP_URL} data-testid="nav-login-button" className="btn-primary !px-5 !py-2.5">
            Login
          </a>
        </nav>
        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}
                className="rounded-md border border-slate-200 p-2 text-slate-700 lg:hidden" aria-label="Toggle menu">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="mx-4 mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="p-4">
              {LINKS.map((l) => (
                <a key={l.testid} href={l.href} onClick={() => setOpen(false)} data-testid={`mobile-${l.testid}`}
                   className="block rounded-lg px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  {l.label}
                </a>
              ))}
              <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                <a href={ERP_URL} data-testid="mobile-nav-login-button"
                   className="block rounded-lg bg-blue-600 px-4 py-3 text-center text-sm font-semibold text-white">
                  Login
                </a>
                {REGISTER_OPTIONS.map((o) => (
                  <a key={o.testid} href={o.href} data-testid={`mobile-${o.testid}`}
                     className="block rounded-lg border border-slate-200 px-4 py-3 text-center text-sm font-semibold text-slate-700">
                    {o.label}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
